import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';

const CreateFunnelModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'draft'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Funnel name is required';
    } else if (formData?.name?.trim()?.length < 3) {
      newErrors.name = 'Funnel name must be at least 3 characters';
    }

    if (formData?.description && formData?.description?.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSubmit?.({
        name: formData?.name?.trim(),
        description: formData?.description?.trim() || null,
        status: formData?.status
      });
    } catch (error) {
      console.error('Error creating funnel:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const funnelTemplates = [
    {
      name: 'Lead Generation Funnel',
      description: 'Capture leads through landing pages and forms',
      steps: ['Landing Page', 'Lead Capture Form', 'Thank You Page', 'Email Follow-up']
    },
    {
      name: 'Sales Funnel',
      description: 'Convert prospects into paying customers',
      steps: ['Awareness', 'Interest', 'Consideration', 'Purchase', 'Retention']
    },
    {
      name: 'Webinar Funnel',
      description: 'Promote and convert through webinars',
      steps: ['Registration Page', 'Confirmation', 'Webinar', 'Pitch', 'Close']
    }
  ];

  const useTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      name: template?.name,
      description: template?.description
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Create New Funnel</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Templates Section */}
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Start Templates</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {funnelTemplates?.map((template, index) => (
              <button
                key={index}
                onClick={() => useTemplate(template)}
                className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
              >
                <div className="font-medium text-sm text-gray-900 mb-1">
                  {template?.name}
                </div>
                <div className="text-xs text-gray-600 mb-2">
                  {template?.description}
                </div>
                <div className="text-xs text-blue-600">
                  {template?.steps?.length} steps
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Funnel Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Funnel Name *
              </label>
              <input
                type="text"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter funnel name"
              />
              {errors?.name && (
                <p className="mt-1 text-sm text-red-600">{errors?.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Describe your funnel purpose and goals"
              />
              {errors?.description && (
                <p className="mt-1 text-sm text-red-600">{errors?.description}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                {formData?.description?.length || 0}/500 characters
              </p>
            </div>

            {/* Initial Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Initial Status
              </label>
              <select
                value={formData?.status}
                onChange={(e) => handleInputChange('status', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                You can change this later from the funnel management page
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData?.name?.trim()}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Create Funnel
                </>
              )}
            </button>
          </div>
        </form>

        {/* Help Text */}
        <div className="px-6 pb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Next Steps</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• After creating, you can add funnel steps and configure lead capture</li>
              <li>• Set up integrations with your email marketing and CRM tools</li>
              <li>• Monitor performance with built-in analytics and optimization recommendations</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateFunnelModal;